/**
 * Single source of truth for the admin portal's information architecture.
 *
 * The whole portal is organised as 3 big GROUPS, each holding 4 small CLUSTERS
 * (12 clusters total). Every cluster owns a short list of pages — this keeps each
 * cluster's left-panel lean. Routes, the HOME dashboard cards, the sidebar nav and
 * the legacy-URL redirects are all derived from `TAXONOMY` below, so the IA lives
 * in exactly one place.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import {
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CpuChipIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'

type Lazy = LazyExoticComponent<ComponentType>
const page = (loader: () => Promise<{ default: ComponentType }>): Lazy => lazy(loader)

// ── Member community ──────────────────────────────────────────────────────────
const Users = page(() => import('@members/pages/UsersPage'))
const UserDetail = page(() => import('@members/pages/UserDetailPage'))
const MemberIntelligence = page(() => import('@members/pages/MemberIntelligencePage'))
const MembershipClaims = page(() => import('@members/pages/MembershipClaimsPage'))
const Mentors = page(() => import('@members/pages/MentorsPage'))
const Roles = page(() => import('@members/pages/RolesPage'))
const Growth = page(() => import('@members/pages/GrowthPage'))
const Brands = page(() => import('@members/pages/BrandsPage'))
const BrandDetail = page(() => import('@members/pages/BrandDetailPage'))
const Schools = page(() => import('@members/pages/SchoolsPage'))
const BankAccounts = page(() => import('@members/pages/BankAccountsPage'))
const Faqs = page(() => import('@members/pages/FaqsPage'))

// ── Trial & leads ─────────────────────────────────────────────────────────────
const Leads = page(() => import('@trial/pages/LeadsPage'))
const LeadScoring = page(() => import('@trial/pages/LeadScoringPage'))
const Enrollments = page(() => import('@trial/pages/EnrollmentsPage'))
const Emails = page(() => import('@trial/pages/EmailsPage'))
const EmailScores = page(() => import('@trial/pages/EmailScoresPage'))
const EmailCatalog = page(() => import('@trial/pages/EmailCatalogPage'))

// ── Content studio ────────────────────────────────────────────────────────────
const Courses = page(() => import('@content/pages/CoursesPage'))
const CourseDetail = page(() => import('@content/pages/CourseDetailPage'))
const Workshops = page(() => import('@content/pages/WorkshopsPage'))
const WorkshopDetail = page(() => import('@content/pages/WorkshopDetailPage'))
const Docs = page(() => import('@content/pages/DocsPage'))
const AiAuditing = page(() => import('@content/pages/AiAuditingPage'))
const Feedback = page(() => import('@content/pages/FeedbackPage'))
const BookEngine = page(() => import('@content/pages/BookEnginePage'))
const BookCreate = page(() => import('@content/pages/BookCreatePage'))
const Ebooks = page(() => import('@content/pages/EbooksPage'))
const EbookDetail = page(() => import('@content/pages/EbookDetailPage'))
const BookQuizManager = page(() => import('@content/pages/BookQuizManagerPage'))
const BookReview = page(() => import('@content/pages/BookReviewPage'))

// ── Learning ──────────────────────────────────────────────────────────────────
const Classes = page(() => import('@learning/pages/ClassesPage'))
const ClassDetail = page(() => import('@learning/pages/ClassDetailPage'))
const Schedule = page(() => import('@learning/pages/SchedulePage'))
const Payments = page(() => import('@learning/pages/PaymentsPage'))
const Issues = page(() => import('@learning/pages/IssuesPage'))
const Certificates = page(() => import('@learning/pages/CertificatesPage'))
const Frameworks = page(() => import('@learning/pages/FrameworksPage'))
const Quizzes = page(() => import('@learning/pages/QuizzesPage'))
const Challenges = page(() => import('@learning/pages/ChallengesPage'))

// ── Marcom ────────────────────────────────────────────────────────────────────
const CourseMarketing = page(() => import('@marcom/pages/CourseMarketingPage'))
const Testimonials = page(() => import('@marcom/pages/TestimonialsPage'))
const Photos = page(() => import('@marcom/pages/MediaLibraryPage'))
const Events = page(() => import('@marcom/pages/EventsPage'))
const EventSeries = page(() => import('@marcom/pages/EventSeriesPage'))
const ZaloGroups = page(() => import('@marcom/pages/ZaloGroupsPage'))
const ZaloContent = page(() => import('@marcom/pages/ZaloContentPage'))
const ZaloPlans = page(() => import('@marcom/pages/ZaloPlansPage'))
const ZaloRules = page(() => import('@marcom/pages/ZaloRulesPage'))
const ZaloReports = page(() => import('@marcom/pages/ZaloReportsPage'))

// ── Platform / ops ────────────────────────────────────────────────────────────
const FeaturePortfolio = page(() => import('./modules/feature-os/FeaturePortfolioPage'))
const ContentFactory = page(() => import('./modules/content-factory/ContentFactoryPage'))
const StyleGuide = page(() => import('@shared/components/layout/StyleGuidePage').then(m => ({ default: m.StyleGuidePage })))

export type IconType = typeof UserGroupIcon

export interface NavItem {
  /** Sidebar / card label. */
  label: string
  /** Absolute route path, e.g. `/community/members`. */
  path: string
  /** Page rendered at `path`. */
  Component: Lazy
  /** Hidden from nav & cards, but still routed (detail pages with params). */
  hidden?: boolean
}

export interface Cluster {
  id: string
  label: string
  /** Landing path — always the first (visible) item's path. */
  basePath: string
  Icon: IconType
  items: NavItem[]
}

export interface Group {
  id: string
  label: string
  basePath: string
  Icon: IconType
  clusters: Cluster[]
}

export const TAXONOMY: Group[] = [
  {
    id: 'community', label: 'Community', basePath: '/community', Icon: UserGroupIcon,
    clusters: [
      {
        id: 'members', label: 'Members', basePath: '/community/members', Icon: UsersIcon,
        items: [
          { label: 'Members', path: '/community/members', Component: Users },
          { label: 'Member Intelligence', path: '/community/members/intelligence', Component: MemberIntelligence },
          { label: 'Membership Activation', path: '/community/members/activation', Component: MembershipClaims },
          { label: 'Member Detail', path: '/community/members/:id', Component: UserDetail, hidden: true },
        ],
      },
      {
        id: 'mentors', label: 'Mentors & Roles', basePath: '/community/mentors', Icon: AcademicCapIcon,
        items: [
          { label: 'Mentors', path: '/community/mentors', Component: Mentors },
          { label: 'Roles', path: '/community/mentors/roles', Component: Roles },
          { label: 'Growth', path: '/community/mentors/growth', Component: Growth },
        ],
      },
      {
        id: 'leads', label: 'Trial & Leads', basePath: '/community/leads', Icon: RocketLaunchIcon,
        items: [
          { label: 'Trial Members', path: '/community/leads', Component: Leads },
          { label: 'Lead Scoring', path: '/community/leads/scoring', Component: LeadScoring },
          { label: 'Enrollments', path: '/community/leads/enrollments', Component: Enrollments },
        ],
      },
      {
        id: 'email', label: 'Email', basePath: '/community/email', Icon: EnvelopeIcon,
        items: [
          { label: 'Email Logs', path: '/community/email', Component: Emails },
          { label: 'Email Scores', path: '/community/email/scores', Component: EmailScores },
          { label: 'Email Catalog', path: '/community/email/catalog', Component: EmailCatalog },
        ],
      },
    ],
  },
  {
    id: 'content', label: 'Content & Learning', basePath: '/content', Icon: BookOpenIcon,
    clusters: [
      {
        id: 'courses', label: 'Courses & Docs', basePath: '/content/courses', Icon: BookOpenIcon,
        items: [
          { label: 'Courses', path: '/content/courses', Component: Courses },
          { label: 'Course Detail', path: '/content/courses/:slug', Component: CourseDetail, hidden: true },
          { label: 'Workshops', path: '/content/workshops', Component: Workshops },
          { label: 'Workshop Detail', path: '/content/workshops/:id', Component: WorkshopDetail, hidden: true },
          { label: 'Docs', path: '/content/docs', Component: Docs },
          { label: 'All Content Audits', path: '/content/audits', Component: AiAuditing },
          { label: 'Feedback', path: '/content/feedback', Component: Feedback },
        ],
      },
      {
        id: 'books', label: 'Book Engine', basePath: '/content/books', Icon: CpuChipIcon,
        items: [
          { label: 'Book Engine', path: '/content/books', Component: BookEngine },
          { label: 'Create Book', path: '/content/books/new', Component: BookCreate },
          { label: 'Book Packages', path: '/content/books/packages', Component: Ebooks },
          { label: 'Book Package Detail', path: '/content/books/packages/:slug', Component: EbookDetail, hidden: true },
          { label: 'Book Quizzes', path: '/content/books/quizzes', Component: BookQuizManager },
          { label: 'Review Studio', path: '/content/books/review', Component: BookReview },
        ],
      },
      {
        id: 'classes', label: 'Classes', basePath: '/content/classes', Icon: CalendarDaysIcon,
        items: [
          { label: 'All Classes', path: '/content/classes', Component: Classes },
          { label: 'Class Detail', path: '/content/classes/:slug', Component: ClassDetail, hidden: true },
          { label: 'Schedule', path: '/content/schedule', Component: Schedule },
          { label: 'Payments', path: '/content/payments', Component: Payments },
          { label: 'Issues', path: '/content/issues', Component: Issues },
        ],
      },
      {
        id: 'assessment', label: 'Assessment', basePath: '/content/assessment', Icon: ClipboardDocumentCheckIcon,
        items: [
          { label: 'Certificates', path: '/content/assessment', Component: Certificates },
          { label: 'Frameworks', path: '/content/assessment/frameworks', Component: Frameworks },
          { label: 'Quizzes', path: '/content/assessment/quizzes', Component: Quizzes },
          { label: 'Challenges', path: '/content/assessment/challenges', Component: Challenges },
        ],
      },
    ],
  },
  {
    id: 'ops', label: 'Marketing & Operations', basePath: '/ops', Icon: MegaphoneIcon,
    clusters: [
      {
        id: 'marketing', label: 'Marketing', basePath: '/ops/marketing', Icon: MegaphoneIcon,
        items: [
          { label: 'Course Marketing', path: '/ops/marketing', Component: CourseMarketing },
          { label: 'Testimonials', path: '/ops/marketing/testimonials', Component: Testimonials },
          { label: 'Photos', path: '/ops/marketing/photos', Component: Photos },
        ],
      },
      {
        id: 'events', label: 'Events', basePath: '/ops/events', Icon: CalendarDaysIcon,
        items: [
          { label: 'Events', path: '/ops/events', Component: Events },
          { label: 'Event Series', path: '/ops/events/series', Component: EventSeries },
        ],
      },
      {
        id: 'zalo', label: 'Zalo', basePath: '/ops/zalo', Icon: ChatBubbleLeftRightIcon,
        items: [
          { label: 'Groups', path: '/ops/zalo', Component: ZaloGroups },
          { label: 'Content', path: '/ops/zalo/content', Component: ZaloContent },
          { label: 'Plans', path: '/ops/zalo/plans', Component: ZaloPlans },
          { label: 'Rules', path: '/ops/zalo/rules', Component: ZaloRules },
          { label: 'Reports', path: '/ops/zalo/reports', Component: ZaloReports },
        ],
      },
      {
        id: 'platform', label: 'Platform & Org', basePath: '/ops/platform', Icon: WrenchScrewdriverIcon,
        items: [
          { label: 'Feature Portfolio', path: '/ops/platform', Component: FeaturePortfolio },
          { label: 'Content Factory', path: '/ops/platform/factory', Component: ContentFactory },
          { label: 'Brands', path: '/ops/platform/brands', Component: Brands },
          { label: 'Brand Detail', path: '/ops/platform/brands/:slug', Component: BrandDetail, hidden: true },
          { label: 'Schools', path: '/ops/platform/schools', Component: Schools },
          { label: 'Bank Accounts', path: '/ops/platform/banks', Component: BankAccounts },
          { label: 'FAQ Management', path: '/ops/platform/faqs', Component: Faqs },
          { label: 'Style Guide', path: '/ops/platform/style-guide', Component: StyleGuide },
        ],
      },
    ],
  },
]

/** Flat list of every route (visible + hidden) for the router. */
export const ALL_ROUTES: NavItem[] = TAXONOMY.flatMap(g => g.clusters.flatMap(c => c.items))

const ALL_CLUSTERS: Array<{ group: Group; cluster: Cluster }> = TAXONOMY.flatMap(group =>
  group.clusters.map(cluster => ({ group, cluster })),
)

/** True when `pathname` sits at `base` or below (on a segment boundary). */
function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + '/')
}

/**
 * Resolve the active group + cluster for a pathname by longest-prefix match
 * across every cluster's concrete (param-free) item paths.
 */
export function findActive(pathname: string): { group: Group; cluster: Cluster } | null {
  let best: { group: Group; cluster: Cluster } | null = null
  let bestLen = -1
  for (const { group, cluster } of ALL_CLUSTERS) {
    for (const item of cluster.items) {
      if (item.path.includes(':')) continue
      if (isUnder(pathname, item.path) && item.path.length > bestLen) {
        best = { group, cluster }
        bestLen = item.path.length
      }
    }
  }
  return best
}

/**
 * Legacy-URL → new-URL redirects so old bookmarks and in-app links keep working
 * after the reorganisation. Order-independent: React Router ranks static paths
 * above the `/*` splat fallbacks.
 */
export const REDIRECTS: Array<[from: string, to: string]> = [
  // members module
  ['/members', '/community/members'],
  ['/members/users', '/community/members'],
  ['/members/member-intelligence', '/community/members/intelligence'],
  ['/members/membership-claims', '/community/members/activation'],
  ['/members/mentors', '/community/mentors'],
  ['/members/roles', '/community/mentors/roles'],
  ['/members/growth', '/community/mentors/growth'],
  ['/members/brands', '/ops/platform/brands'],
  ['/members/schools', '/ops/platform/schools'],
  ['/members/bank-accounts', '/ops/platform/banks'],
  ['/members/faqs', '/ops/platform/faqs'],
  ['/members/style-guide', '/ops/platform/style-guide'],
  ['/members/*', '/community/members'],
  // trial module
  ['/trial', '/community/leads'],
  ['/trial/leads', '/community/leads'],
  ['/trial/lead-scoring', '/community/leads/scoring'],
  ['/trial/enrollments', '/community/leads/enrollments'],
  ['/trial/emails', '/community/email'],
  ['/trial/email-scores', '/community/email/scores'],
  ['/trial/email-catalog', '/community/email/catalog'],
  ['/trial/*', '/community/leads'],
  // content studio module
  ['/course-content', '/content/courses'],
  ['/course-content/courses', '/content/courses'],
  ['/course-content/workshops', '/content/workshops'],
  ['/course-content/docs', '/content/docs'],
  ['/course-content/ai-auditing', '/content/audits'],
  ['/course-content/feedback', '/content/feedback'],
  ['/course-content/book-engine', '/content/books'],
  ['/course-content/book-engine/new', '/content/books/new'],
  ['/course-content/ebooks', '/content/books/packages'],
  ['/course-content/book-quizzes', '/content/books/quizzes'],
  ['/course-content/book-review', '/content/books/review'],
  ['/course-content/*', '/content/courses'],
  ['/workshops', '/content/workshops'],
  ['/workshops/*', '/content/workshops'],
  // learning module
  ['/learning', '/content/classes'],
  ['/learning/classes', '/content/classes'],
  ['/learning/schedule', '/content/schedule'],
  ['/learning/payments', '/content/payments'],
  ['/learning/issues', '/content/issues'],
  ['/learning/certificates', '/content/assessment'],
  ['/learning/frameworks', '/content/assessment/frameworks'],
  ['/learning/quizzes', '/content/assessment/quizzes'],
  ['/learning/challenges', '/content/assessment/challenges'],
  ['/learning/*', '/content/classes'],
  // marcom module
  ['/marcom', '/ops/marketing'],
  ['/marcom/courses', '/ops/marketing'],
  ['/marcom/testimonials', '/ops/marketing/testimonials'],
  ['/marcom/photos', '/ops/marketing/photos'],
  ['/marcom/events', '/ops/events'],
  ['/marcom/event-series', '/ops/events/series'],
  ['/marcom/zalo/groups', '/ops/zalo'],
  ['/marcom/zalo/content', '/ops/zalo/content'],
  ['/marcom/zalo/plans', '/ops/zalo/plans'],
  ['/marcom/zalo/rules', '/ops/zalo/rules'],
  ['/marcom/zalo/reports', '/ops/zalo/reports'],
  ['/marcom/*', '/ops/marketing'],
  // platform modules
  ['/feature-os', '/ops/platform'],
  ['/feature-os/*', '/ops/platform'],
  ['/content-factory', '/ops/platform/factory'],
  ['/content-factory/*', '/ops/platform/factory'],
]
